import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Tooltip,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import ViewListIcon from '@material-ui/icons/ViewList';
import CategoryIcon from '@material-ui/icons/Category';
import AddShoppingCartIcon from '@material-ui/icons/AddShoppingCart';
import { formattedCurrency, normalizeText } from '../../../utils/utils';
import { usePOS } from '../../../contexts/POSContext';
import { searchCatalogFuzzy } from '../../../utils/posFuzzyEngine';
import POSItemThumbnail from '../POSItemThumbnail';
import UIDialogTitle from '../../UI/Dialog/Title/UIDialogTitle';

const WINDOW_SIZE = 60;
const ADVANCE_STEP = 40;
const REMOVE_STEP = 20;

const POSSearchModal = ({ open, onClose, initialSearchTerm = '' }) => {
  const { products, services, indexedCatalog, categories = [], addItem, isDarkMode } = usePOS();
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm || '');
  const [debouncedTerm, setDebouncedTerm] = useState(initialSearchTerm || '');
  const [selectedType, setSelectedType] = useState('all'); // 'all' | 'products' | 'services'
  const [selectedCategory, setSelectedCategory] = useState('all'); // 'all' | category_id
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem('duzz_pdv_search_view_mode') || 'grid';
    } catch (e) {
      return 'grid';
    }
  });
  const [range, setRange] = useState({ start: 0, end: WINDOW_SIZE });
  const inputRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // Armazenar preferência de modo de visualização (Grade / Lista) no cache
  const handleToggleViewMode = (mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem('duzz_pdv_search_view_mode', mode);
    } catch (e) {
      // noop
    }
  };

  useEffect(() => {
    if (open) {
      setSearchTerm(initialSearchTerm || '');
      setDebouncedTerm(initialSearchTerm || '');
      setRange({ start: 0, end: WINDOW_SIZE });
      setTimeout(() => {
        if (inputRef.current) inputRef.current.focus();
      }, 100);
    }
  }, [open, initialSearchTerm]);

  // Debounce de 150ms na digitação para garantir 60 FPS e busca suave
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedTerm(searchTerm);
    }, 150);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Reseta a janela deslizante ao alterar termo, tipo, categoria ou modo de visualização
  useEffect(() => {
    setRange({ start: 0, end: WINDOW_SIZE });
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [debouncedTerm, selectedType, selectedCategory, viewMode]);

  // Categorias disponíveis filtradas pelo tipo selecionado
  const availableCategories = useMemo(() => {
    let cats = categories || [];
    if (selectedType === 'products') {
      cats = cats.filter((c) => c.type === 'product');
    } else if (selectedType === 'services') {
      cats = cats.filter((c) => c.type === 'service');
    }
    // Remove duplicatas por ID se houver
    const uniqueMap = new Map();
    cats.forEach((c) => {
      if (c && c.id && !uniqueMap.has(String(c.id))) {
        uniqueMap.set(String(c.id), c);
      }
    });
    return Array.from(uniqueMap.values());
  }, [categories, selectedType]);

  const parsedMultiplier = useMemo(() => {
    const raw = (debouncedTerm || '').trim();
    if (raw.includes('*')) {
      const parts = raw.split('*');
      if (parts.length === 2) {
        const m = parseFloat(parts[0].replace(',', '.'));
        if (!isNaN(m) && m > 0) return { multiplier: m, cleanTerm: parts[1].trim() };
      }
    }
    return { multiplier: 1, cleanTerm: raw };
  }, [debouncedTerm]);

  const filteredList = useMemo(() => {
    const term = parsedMultiplier.cleanTerm;

    // Motor fonético em cascata com suporte a tipo e categoria
    if (indexedCatalog && indexedCatalog.length > 0) {
      return searchCatalogFuzzy(term, indexedCatalog, selectedType, selectedCategory);
    }

    // Fallback: mapeia diretamente de products e services caso ainda não indexado
    let list = [];
    if (selectedType === 'all' || selectedType === 'products') {
      const mappedProds = products.map((p) => {
        const catId = p.category && typeof p.category === 'object' ? String(p.category.id) : (p.category ? String(p.category) : '');
        const catName = (p.category && typeof p.category === 'object' && p.category.name) || '';
        return {
          id: p.id,
          name: p.name,
          barCode: p.barCode,
          valueUn: parseFloat(p.value || 0),
          typeItem: 'product',
          category: p.category,
          categoryId: catId,
          categoryName: catName,
          amount: parseFloat(p.amount || 0),
          images: p.images,
        };
      });
      list = [...list, ...mappedProds];
    }

    if (selectedType === 'all' || selectedType === 'services') {
      const mappedServs = services.map((s) => {
        const catId = s.category && typeof s.category === 'object' ? String(s.category.id) : (s.category ? String(s.category) : '');
        const catName = (s.category && typeof s.category === 'object' && s.category.name) || '';
        return {
          id: s.id,
          name: s.name,
          barCode: s.barCode,
          valueUn: parseFloat(s.value || 0),
          typeItem: 'service',
          category: s.category,
          categoryId: catId,
          categoryName: catName,
          amount: 999999,
          images: s.images,
        };
      });
      list = [...list, ...mappedServs];
    }

    if (selectedCategory && selectedCategory !== 'all') {
      list = list.filter((it) => it.categoryId === String(selectedCategory));
    }

    if (!term) return list;

    const norm = normalizeText(term);
    return list.filter((item) => (
      String(item.id).includes(norm) ||
      normalizeText(item.name).includes(norm) ||
      normalizeText(item.barCode).includes(norm)
    ));
  }, [indexedCatalog, products, services, parsedMultiplier.cleanTerm, selectedType, selectedCategory]);

  // Itens visíveis dentro da janela deslizante (avança +40, remove -20 do início)
  const displayedList = useMemo(() => {
    return filteredList.slice(range.start, range.end);
  }, [filteredList, range]);

  const handleScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;

    // Rolagem para baixo: avança +40 no fim e remove -20 do início
    if (scrollTop + clientHeight >= scrollHeight - 60) {
      if (range.end < filteredList.length) {
        setRange((prev) => {
          const nextEnd = Math.min(prev.end + ADVANCE_STEP, filteredList.length);
          const nextStart = Math.min(prev.start + REMOVE_STEP, Math.max(0, nextEnd - WINDOW_SIZE));
          return { start: nextStart, end: nextEnd };
        });
      }
    }

    // Rolagem para cima: recupera os itens anteriores caso tenha removido do início
    if (scrollTop <= 30 && range.start > 0) {
      setRange((prev) => {
        const nextStart = Math.max(0, prev.start - REMOVE_STEP);
        const nextEnd = Math.max(WINDOW_SIZE, prev.end - REMOVE_STEP);
        return { start: nextStart, end: nextEnd };
      });
    }
  };

  const handleSelectItem = (item) => {
    addItem(item, parsedMultiplier.multiplier || 1);
    onClose();
  };

  const totalCatalogCount = products.length + services.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ className: isDarkMode ? 'pos-dark-paper' : '' }}
    >
      <UIDialogTitle onClose={onClose}>
        Pesquisar Produtos e Serviços [F2]
      </UIDialogTitle>

      <DialogContent dividers style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 18px' }}>
        {/* Campo de Busca Rápida com Atalho */}
        <TextField
          inputRef={inputRef}
          placeholder="Digite o nome, código ou ID do item (Enter para selecionar o primeiro)..."
          variant="outlined"
          fullWidth
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && displayedList.length > 0) {
              e.preventDefault();
              handleSelectItem(displayedList[0]);
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        {/* Linha de Filtros Principais: Tipos de Cadastro + Modos de Visualização (Grade / Lista) */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          {/* Filtro por Tipo */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip
              label={`Todos (${totalCatalogCount})`}
              size="small"
              clickable
              color={selectedType === 'all' ? 'primary' : 'default'}
              onClick={() => {
                setSelectedType('all');
                setSelectedCategory('all');
              }}
              style={{
                backgroundColor: selectedType === 'all' ? '#17497F' : undefined,
                color: selectedType === 'all' ? '#ffffff' : undefined,
                fontWeight: 600,
                fontSize: '0.78rem',
              }}
            />
            <Chip
              label={`Produtos (${products.length})`}
              size="small"
              clickable
              onClick={() => {
                setSelectedType('products');
                setSelectedCategory('all');
              }}
              style={{
                backgroundColor: selectedType === 'products' ? '#17497F' : undefined,
                color: selectedType === 'products' ? '#ffffff' : undefined,
                fontWeight: 600,
                fontSize: '0.78rem',
              }}
            />
            <Chip
              label={`Serviços (${services.length})`}
              size="small"
              clickable
              onClick={() => {
                setSelectedType('services');
                setSelectedCategory('all');
              }}
              style={{
                backgroundColor: selectedType === 'services' ? '#17497F' : undefined,
                color: selectedType === 'services' ? '#ffffff' : undefined,
                fontWeight: 600,
                fontSize: '0.78rem',
              }}
            />
          </div>

          {/* Seletor de Modo de Visualização (Grade / Lista) & Contador */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: '0.78rem', color: isDarkMode ? '#94a3b8' : '#64748b' }}>
              <strong>{range.start + 1} - {Math.min(range.end, filteredList.length)}</strong> de <strong>{filteredList.length}</strong> itens
            </span>

            <div className="pos-view-mode-toggle" title="Alterar modo de visualização do catálogo">
              <Tooltip title="Modo Grade (Imagens Grandes para Acessibilidade)">
                <button
                  type="button"
                  className={`pos-view-mode-btn ${viewMode === 'grid' ? 'active' : ''}`}
                  onClick={() => handleToggleViewMode('grid')}
                >
                  <ViewModuleIcon style={{ fontSize: 18 }} />
                  Grade
                </button>
              </Tooltip>
              <Tooltip title="Modo Lista (Tabela Detalhada)">
                <button
                  type="button"
                  className={`pos-view-mode-btn ${viewMode === 'table' ? 'active' : ''}`}
                  onClick={() => handleToggleViewMode('table')}
                >
                  <ViewListIcon style={{ fontSize: 18 }} />
                  Lista
                </button>
              </Tooltip>
            </div>
          </div>
        </div>

        {/* Barra de Chips de Navegação entre Categorias */}
        {availableCategories.length > 0 && (
          <div className="pos-chips-scroll-bar">
            <button
              type="button"
              className={`pos-category-chip-btn ${selectedCategory === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('all')}
            >
              <CategoryIcon style={{ fontSize: 14 }} />
              Todas Categorias
              <span className="pos-category-chip-badge">{filteredList.length}</span>
            </button>

            {availableCategories.map((cat) => {
              const isCatActive = selectedCategory === String(cat.id);
              return (
                <button
                  key={`cat-chip-${cat.type}-${cat.id}`}
                  type="button"
                  className={`pos-category-chip-btn ${isCatActive ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(isCatActive ? 'all' : String(cat.id))}
                >
                  {cat.name}
                  {cat.count !== undefined && (
                    <span className="pos-category-chip-badge">{cat.count}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Conteúdo Principal: Modo Grade (Cards com Imagens Grandes) ou Modo Lista (Tabela) */}
        {viewMode === 'grid' ? (
          <div
            ref={scrollContainerRef}
            className="pos-grid-catalog-container"
            onScroll={handleScroll}
          >
            {/* Indicador de itens anteriores */}
            {range.start > 0 && (
              <div style={{ textAlign: 'center', padding: '6px 12px', marginBottom: 10, backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#e2e8f0', color: isDarkMode ? '#94a3b8' : '#475569', fontSize: '0.78rem', borderRadius: 4 }}>
                ▲ Role para cima para ver os primeiros {range.start} itens
              </div>
            )}

            {displayedList.length > 0 ? (
              <div className="pos-grid-catalog">
                {displayedList.map((item) => {
                  const isOutOfStock = item.typeItem === 'product' && item.amount <= 0;
                  return (
                    <div
                      key={`${item.typeItem}-${item.id}`}
                      className="pos-grid-card"
                      onClick={() => handleSelectItem(item)}
                      title={`Clique para inserir ${item.name}`}
                    >
                      <div className="pos-grid-card-thumb-wrapper">
                        <POSItemThumbnail
                          images={item.images}
                          name={item.name}
                          typeItem={item.typeItem}
                          size={110}
                          style={{ width: '100%', height: 110, borderRadius: 6 }}
                          isDarkMode={isDarkMode}
                        />
                      </div>

                      <div className="pos-grid-card-body">
                        <div className="pos-grid-card-tags">
                          <span
                            style={{
                              padding: '1px 5px',
                              borderRadius: 3,
                              fontSize: '0.68rem',
                              backgroundColor: item.typeItem === 'product' ? (isDarkMode ? 'rgba(30, 64, 175, 0.3)' : '#e3f2fd') : (isDarkMode ? 'rgba(126, 34, 206, 0.3)' : '#f3e5f5'),
                              color: item.typeItem === 'product' ? (isDarkMode ? '#93c5fd' : '#1565c0') : (isDarkMode ? '#d8b4fe' : '#7b1fa2'),
                              fontWeight: 700,
                            }}
                          >
                            {item.typeItem === 'product' ? 'Produto' : 'Serviço'}
                          </span>

                          {item.categoryName && (
                            <span
                              style={{
                                fontSize: '0.68rem',
                                color: isDarkMode ? '#94a3b8' : '#64748b',
                                maxWidth: '90px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={item.categoryName}
                            >
                              {item.categoryName}
                            </span>
                          )}
                        </div>

                        <div className="pos-grid-card-title">
                          {item.name}
                        </div>

                        <div className="pos-grid-card-meta">
                          {item.barCode ? `Cód: ${item.barCode} • ` : ''}ID: #{item.id}
                        </div>
                      </div>

                      <div className="pos-grid-card-footer">
                        <div className="pos-grid-card-price">
                          {formattedCurrency(item.valueUn)}
                        </div>

                        {item.typeItem === 'product' && (
                          <span className={`pos-grid-card-stock ${isOutOfStock ? 'danger' : ''}`}>
                            {isOutOfStock ? 'Esgotado' : `${item.amount} un`}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 40, color: '#9e9e9e' }}>
                Nenhum item encontrado com o termo ou categoria selecionada.
              </div>
            )}

            {/* Indicador de itens restantes */}
            {range.end < filteredList.length && (
              <div style={{ textAlign: 'center', padding: 14, color: '#94a3b8' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: '0.8rem' }}>
                  <CircularProgress size={16} />
                  Role para carregar mais itens (+{filteredList.length - range.end} restantes)
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Modo Lista (Tabela Tradicional) */
          <TableContainer
            ref={scrollContainerRef}
            onScroll={handleScroll}
            style={{
              maxHeight: 440,
              border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
              borderRadius: 6,
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell style={{ fontWeight: 700 }}>Item / Descrição</TableCell>
                  <TableCell style={{ width: 110, fontWeight: 700 }}>Categoria</TableCell>
                  <TableCell style={{ width: 90, fontWeight: 700 }}>Tipo</TableCell>
                  <TableCell style={{ width: 85, textAlign: 'right', fontWeight: 700 }}>Estoque</TableCell>
                  <TableCell style={{ width: 110, textAlign: 'right', fontWeight: 700 }}>Preço</TableCell>
                  <TableCell style={{ width: 90, textAlign: 'center', fontWeight: 700 }}>Ação</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Indicador de itens anteriores removidos do início */}
                {range.start > 0 && (
                  <TableRow>
                    <TableCell colSpan={6} style={{ textAlign: 'center', padding: '6px 12px', backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#f8fafc', color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '0.78rem' }}>
                      ▲ Role para cima para ver os primeiros {range.start} itens
                    </TableCell>
                  </TableRow>
                )}

                {displayedList.map((item) => (
                  <TableRow
                    key={`${item.typeItem}-${item.id}`}
                    hover
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSelectItem(item)}
                  >
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <POSItemThumbnail
                          images={item.images}
                          name={item.name}
                          typeItem={item.typeItem}
                          size={46}
                          isDarkMode={isDarkMode}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                          <span style={{ fontSize: '0.92rem', fontWeight: 600, color: isDarkMode ? '#f8fafc' : '#212121', wordBreak: 'break-word' }}>
                            {item.name}
                          </span>
                          <div style={{ fontSize: '0.75rem', color: isDarkMode ? '#94a3b8' : '#757575' }}>
                            {item.barCode ? `Cód. Barras: ${item.barCode} • ` : ''}
                            ID: #{item.id}
                          </div>
                        </div>
                      </div>
                    </TableCell>

                    <TableCell>
                      <span style={{ fontSize: '0.8rem', color: isDarkMode ? '#cbd5e1' : '#475569' }}>
                        {item.categoryName || '—'}
                      </span>
                    </TableCell>

                    <TableCell>
                      <span
                        style={{
                          padding: '2px 6px',
                          borderRadius: 3,
                          fontSize: '0.72rem',
                          backgroundColor: item.typeItem === 'product' ? (isDarkMode ? 'rgba(30, 64, 175, 0.3)' : '#e3f2fd') : (isDarkMode ? 'rgba(126, 34, 206, 0.3)' : '#f3e5f5'),
                          color: item.typeItem === 'product' ? (isDarkMode ? '#93c5fd' : '#1565c0') : (isDarkMode ? '#d8b4fe' : '#7b1fa2'),
                          fontWeight: 600,
                        }}
                      >
                        {item.typeItem === 'product' ? 'Produto' : 'Serviço'}
                      </span>
                    </TableCell>

                    <TableCell style={{ textAlign: 'right' }}>
                      {item.typeItem === 'product' ? (
                        <span
                          style={{
                            fontWeight: 600,
                            color: item.amount <= 0 ? '#ef4444' : (isDarkMode ? '#cbd5e1' : '#424242'),
                          }}
                        >
                          {item.amount}
                        </span>
                      ) : (
                        <span style={{ color: '#9e9e9e' }}>—</span>
                      )}
                    </TableCell>

                    <TableCell style={{ textAlign: 'right', fontWeight: 700, color: isDarkMode ? '#4ade80' : '#185421', fontSize: '0.95rem' }}>
                      {formattedCurrency(item.valueUn)}
                    </TableCell>

                    <TableCell style={{ textAlign: 'center' }}>
                      <Button
                        size="small"
                        variant="contained"
                        style={{
                          backgroundColor: '#17497F',
                          color: '#fff',
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          padding: '3px 8px',
                        }}
                      >
                        Inserir
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}

                {displayedList.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} style={{ textAlign: 'center', padding: 32, color: '#9e9e9e' }}>
                      Nenhum item encontrado com o termo ou categoria selecionada.
                    </TableCell>
                  </TableRow>
                )}

                {/* Indicador de itens restantes no final */}
                {range.end < filteredList.length && (
                  <TableRow>
                    <TableCell colSpan={6} style={{ textAlign: 'center', padding: 12, color: '#94a3b8' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: '0.8rem' }}>
                        <CircularProgress size={16} />
                        Role para carregar mais itens (+{filteredList.length - range.end} restantes)
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions style={{ padding: '8px 18px', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '0.75rem', color: isDarkMode ? '#94a3b8' : '#757575' }}>
          Dica: Pressione <strong>Enter</strong> para selecionar o 1º item ou clique diretamente no card/linha.
        </span>
        <Button onClick={onClose} color="default">
          Fechar [Esc]
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default POSSearchModal;

